import React, { ReactNode, useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import {
  Appearance,
  StripeElementsOptions,
  loadStripe,
} from "@stripe/stripe-js";
import { useIsMounted } from "@campgladiator/cg-common.utility.hooks.is-mounted";
import {
  fetchStripeCreateSetupIntent,
  fetchStripePublishableKey,
} from "@campgladiator/cg-common.api.stripe";

type StripeElementsConfigProps = {
  customerId: string;
  children: ReactNode;
};

const getAppearance = (): Appearance => ({
  theme: "stripe",
  variables: {
    borderRadius: "100px",
    fontSizeBase: "14px",
    fontLineHeight: "18px",
  },
  rules: {
    ".Label": {
      color: "#333333",
      fontWeight: "600",
      fontSize: "14px",
      marginTop: "5px",
      marginBottom: "7px",
    },
    ".Input": {
      boxShadow: "none",
      paddingLeft: "20px",
      paddingRight: "20px",
      borderColor: "#656566",
      border: "1px solid",
      outline: "none",
      fontWeight: "bold",
    },
  },
});

const fetchClientSecret = async (
  customerId: string,
  isMounted: () => boolean
) => {
  const stripe = await fetchStripeCreateSetupIntent(customerId);
  if (isMounted()) {
    return stripe.clientSecret;
  }
};

const fetchStripePromise = async (isMounted: () => boolean) => {
  const { stripePublishableKey } = await fetchStripePublishableKey();
  if (isMounted()) {
    return loadStripe(stripePublishableKey);
  }
};

const StripeElementsConfig = ({
  customerId,
  children,
}: StripeElementsConfigProps) => {
  const [clientSecret, setClientSecret] = useState<string>("");
  const [stripePromise, setStripePromise] = useState<any>(null);
  const isMounted = useIsMounted();

  const appearance = getAppearance();

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: appearance,
  };

  useEffect(() => {
    fetchClientSecret(customerId, isMounted).then((clientSecret) => {
      if (clientSecret) {
        setClientSecret(clientSecret);
      }
    });
  }, [customerId]);

  useEffect(() => {
    fetchStripePromise(isMounted).then((stripePromise) => {
      if (stripePromise) {
        setStripePromise(stripePromise);
      }
    });
  }, []);

  return (
    <>
      {clientSecret && stripePromise && (
        <Elements options={options} stripe={stripePromise}>
          {children}
        </Elements>
      )}
    </>
  );
};

export default StripeElementsConfig;
